import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "../../../auth/useAuth";
import { ADMIN_PERMISSIONS } from "../../../auth/adminPermissions";
import AdminPageLayout from "../../../components/midpoint/admin/layout/AdminPageLayout";
import AdminPageHeader from "../../../components/midpoint/admin/layout/AdminPageHeader";
import { AdminAutomotiveNav } from "../automotive/AdminAutomotiveNav";
import {
  BILLING_STATUS_LABELS,
  PLAN_KEYS,
  PLAN_LABELS,
  PLAN_NOTE_TYPE_LABELS,
  PLAN_STATUS_LABELS,
  REPORTS_ACCESS_LABELS,
  formatPlanLimit,
  planBadgeClass,
  trackDealerPlanEvent,
} from "../../../constants/automapDealerPlanConstants";
import {
  addDealerPlanNote,
  assignDealerPlan,
  cancelDealerPlan,
  convertTrialToPlan,
  fetchAdminDealerPlan,
  suspendDealerPlan,
  updateCustomPlanLimits,
  updateDealerPlanBilling,
} from "../../../services/automapDealerPlanApi";
import { AdminPlansNav, PlanActionBar } from "./AdminPlansShared";

export default function AdminDealerPlanDetailPage() {
  const { dealerId } = useParams();
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManage = isSuperAdmin || hasPermission(ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_MANAGE);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [planKey, setPlanKey] = useState("starter");
  const [noteBody, setNoteBody] = useState("");
  const [customListingLimit, setCustomListingLimit] = useState("");
  const [customLeadLimit, setCustomLeadLimit] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const payload = await fetchAdminDealerPlan(dealerId);
      setData(payload);
      trackDealerPlanEvent("admin_dealer_plan_viewed", { dealerId });
    } catch (err) {
      setError(err.message || "Error al cargar plan.");
    } finally {
      setLoading(false);
    }
  }, [dealerId]);

  useEffect(() => {
    load();
  }, [load]);

  const runAssign = async () => {
    try {
      await assignDealerPlan(dealerId, { planKey, billingStatus: "pending_manual_invoice" });
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const runConvertTrial = async () => {
    try {
      await convertTrialToPlan(dealerId, { planKey, pendingInvoice: true });
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const runCustomLimits = async () => {
    try {
      await updateCustomPlanLimits(dealerId, {
        planKey: "custom",
        customListingLimit: customListingLimit ? Number(customListingLimit) : null,
        customLeadLimitMonthly: customLeadLimit ? Number(customLeadLimit) : null,
      });
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const runNote = async () => {
    if (!noteBody.trim()) return;
    await addDealerPlanNote(dealerId, { body: noteBody, noteType: "general" });
    setNoteBody("");
    await load();
  };

  if (loading) return <AdminPageLayout><p className="dealer-state-box">Cargando plan…</p></AdminPageLayout>;
  if (error && !data) return <AdminPageLayout><p className="dealer-state-box">{error}</p></AdminPageLayout>;
  if (!data) return null;

  const { dealer, plan, usage, trial, events, notes, featureAccess, billingPlaceholder } = data;

  return (
    <AdminPageLayout>
      <AdminPageHeader
        eyebrow="AutoMapa · Plan concesionario"
        title={dealer.businessName}
        description={`Plan, límites y billing placeholder · ${dealer.city || ""}`}
      />
      <AdminAutomotiveNav active="plans" />
      <AdminPlansNav active="dealers" />
      <main className="admin-console__main wrap">
        {error ? <p className="dealer-state-box">{error}</p> : null}

        <section className="dealer-state-box">
          <h3>Plan actual</h3>
          <p>
            <span className={`admin-badge ${planBadgeClass(plan.planKey)}`}>{plan.publicName}</span>
            {" · "}
            {PLAN_STATUS_LABELS[plan.assignment?.status] || "Activo"}
            {" · "}
            Billing: {BILLING_STATUS_LABELS[billingPlaceholder.status] || billingPlaceholder.status}
          </p>
          <div className="dashboard-lead-stats">
            <span>Inicio: {plan.assignment?.effectiveStartAt?.slice(0, 10) || "—"}</span>
            <span>Fin: {plan.assignment?.effectiveEndAt?.slice(0, 10) || "—"}</span>
            <span>Listings: {usage.activeListings}/{formatPlanLimit(usage.listingLimit)}</span>
            <span>Leads mes: {usage.leadsThisMonth}/{formatPlanLimit(usage.leadLimitMonthly)}</span>
            <span>Reportes: {REPORTS_ACCESS_LABELS[featureAccess.performanceReports] || "—"}</span>
            <span>Destacado: {featureAccess.featuredPlacement ? "Sí" : "No"}</span>
            <Link to="/admin/placements/listings">Gestionar placements →</Link>
          </div>
          <p className="muted">{billingPlaceholder.message}</p>
        </section>

        {trial ? (
          <section className="dealer-state-box">
            <h3>Contexto trial</h3>
            <p>Trial: {trial.trialStatus} · Stage: {trial.conversionStage} · Interés plan: {trial.expectedPlan || "—"}</p>
            {canManage ? (
              <div className="import-actions">
                <select value={planKey} onChange={(e) => setPlanKey(e.target.value)}>
                  {PLAN_KEYS.filter((k) => k !== "free").map((k) => (
                    <option key={k} value={k}>{PLAN_LABELS[k]}</option>
                  ))}
                </select>
                <button type="button" className="admin-btn admin-btn--primary" onClick={runConvertTrial}>Convertir a plan pago</button>
              </div>
            ) : null}
          </section>
        ) : null}

        {canManage ? (
          <>
            <section className="dealer-state-box">
              <h3>Acciones</h3>
              <div className="import-actions" style={{ marginBottom: "0.75rem" }}>
                <select value={planKey} onChange={(e) => setPlanKey(e.target.value)}>
                  {PLAN_KEYS.map((k) => <option key={k} value={k}>{PLAN_LABELS[k]}</option>)}
                </select>
                <button type="button" className="admin-btn admin-btn--primary" onClick={runAssign}>Asignar / cambiar</button>
                <button type="button" className="admin-btn" onClick={() => updateDealerPlanBilling(dealerId, "paid_manual").then(load)}>Marcar pagado manual</button>
                <button type="button" className="admin-btn" onClick={() => updateDealerPlanBilling(dealerId, "pending_manual_invoice").then(load)}>Factura pendiente</button>
              </div>
              <div className="import-actions" style={{ marginBottom: "0.75rem" }}>
                <input placeholder="Límite listings custom" value={customListingLimit} onChange={(e) => setCustomListingLimit(e.target.value)} />
                <input placeholder="Límite leads custom" value={customLeadLimit} onChange={(e) => setCustomLeadLimit(e.target.value)} />
                <button type="button" className="admin-btn" onClick={runCustomLimits}>Aplicar límites custom</button>
              </div>
              <PlanActionBar
                dealerId={dealerId}
                canManage={canManage}
                onAssign={runAssign}
                onSuspend={() => suspendDealerPlan(dealerId, "Suspendido por admin").then(load)}
                onCancel={() => cancelDealerPlan(dealerId, "Cancelado por admin").then(load)}
              />
            </section>
            <section className="dealer-state-box">
              <h3>Notas internas</h3>
              <textarea value={noteBody} onChange={(e) => setNoteBody(e.target.value)} rows={3} style={{ width: "100%" }} />
              <button type="button" className="admin-btn" onClick={runNote}>Agregar nota</button>
              <ul>
                {(notes || []).map((n) => (
                  <li key={n.id}>{PLAN_NOTE_TYPE_LABELS[n.noteType] || n.noteType}: {n.body} · {n.createdAt?.slice(0, 10)}</li>
                ))}
              </ul>
            </section>
          </>
        ) : null}

        <section className="dealer-state-box">
          <h3>Historial</h3>
          {!events?.length ? <p className="muted">Sin eventos.</p> : (
            <ul>
              {events.map((e) => (
                <li key={e.id}>
                  {e.eventType} — {e.description || ""} · {e.createdAt?.slice(0, 16)}
                  {e.previousPlanKey && e.newPlanKey ? ` (${e.previousPlanKey} → ${e.newPlanKey})` : ""}
                </li>
              ))}
            </ul>
          )}
        </section>

        <p><Link to={`/admin/dealers/${dealerId}`}>← Volver al concesionario</Link></p>
      </main>
    </AdminPageLayout>
  );
}
